import { useAuthStore } from '../../stores/auth'
import { apiUrl } from '../../api/http'
import { applyRankedCalls, armRankedCalls, readRankedCalls } from './rankedCall'

// Vs-AI runs in the browser. While a local duel is active this posts a
// public pulse so the admin panel can list it. Failures never affect play.

const HEARTBEAT_MS = 4_000

export type LiveAiKill = 'time' | 'admin'

export type LiveAiPulse = {
  turn: number
  phase: string
  turnSeat: 0 | 1
  difficulty: string
  lifePoints: [number, number]
  hand: [number, number]
  deck: [number, number]
  cpuName: string
  /** Match start on this client. The server uses it to tell one duel from the next. */
  begunAt: number
}

let tracking = false
let latest: LiveAiPulse | null = null
let timer: ReturnType<typeof setTimeout> | null = null
let lastSent = 0
let onKill: ((reason: LiveAiKill) => void) | null = null

export function setLiveAiKillHandler(handler: ((reason: LiveAiKill) => void) | null): void {
  onKill = handler
}

function post(body: unknown, keepalive: boolean): void {
  try {
    const token = useAuthStore().token
    if (!token || typeof window === 'undefined') return
    const active = Boolean(
      body && typeof body === 'object' && (body as { active?: unknown }).active !== false,
    )
    void fetch(apiUrl('/account/live-duel'), {
      method: 'POST',
      keepalive,
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
    })
      .then(async (res) => {
        if (!res.ok || !active || !tracking) return
        let data: { kill?: unknown } | null = null
        try {
          data = (await res.json()) as { kill?: unknown }
        } catch {
          return
        }
        if (!tracking) return
        if (data?.kill === 'time' || data?.kill === 'admin') {
          applyRankedCalls([])
          onKill?.(data.kill)
          return
        }
        applyRankedCalls(readRankedCalls(data))
      })
      .catch(() => undefined)
  } catch {
    /* presence must never break a duel */
  }
}

function arm(delay: number): void {
  if (timer || !tracking) return
  timer = setTimeout(() => {
    timer = null
    flush()
  }, delay)
}

function flush(): void {
  if (!tracking || !latest) return
  lastSent = Date.now()
  post({ active: true, ...latest }, false)
  arm(HEARTBEAT_MS)
}

export function noteLiveAiDuel(pulse: LiveAiPulse): void {
  try {
    armRankedCalls(true)
    latest = pulse
    tracking = true
    const due = HEARTBEAT_MS - (Date.now() - lastSent)
    if (due <= 0) {
      if (timer) {
        clearTimeout(timer)
        timer = null
      }
      flush()
      return
    }
    arm(due)
  } catch {
    /* presence must never break a duel */
  }
}

export function clearLiveAiDuel(): void {
  armRankedCalls(false)
  if (!tracking) return
  tracking = false
  latest = null
  lastSent = 0
  if (timer) {
    clearTimeout(timer)
    timer = null
  }
  post({ active: false }, true)
}

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    clearLiveAiDuel()
  })
}
